import { Button } from '../ui/button'
import { Smartphone, Download, Copy, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import * as QRCode from 'qrcode'
import { API_KEYS_QUERY_KEY } from '@/lib/queries'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { notify } from '@/lib/notify'

interface DeviceRegistrationApiKey {
  id: string
  name: string
  apiKey: string
  usageCount: number
  lastUsedAt?: string
  createdAt: string
}

export function SmsDeviceSetupContent({ onDone }: { onDone?: () => void }) {
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('')
  const [deviceApiKey, setDeviceApiKey] = useState<DeviceRegistrationApiKey | null>(null)
  const [fullApiKey, setFullApiKey] = useState<string>('')
  const queryClient = useQueryClient()

  // Generate device registration API key
  const { mutate: generateDeviceKey, isPending: isGeneratingKey } = useMutation({
    mutationKey: ['generateDeviceRegistrationKey'],
    mutationFn: async () => {
      const response = await api.post('/auth/api-keys/device-registration')
      return response.data.data
    },
    onSuccess: async (data) => {
      const { apiKey, apiKeyDoc } = data
      setDeviceApiKey(apiKeyDoc)
      setFullApiKey(apiKey) // Store the full API key for copying
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY })

      // Generate QR code with the full API key
      try {
        const qrDataURL = await QRCode.toDataURL(apiKey, {
          width: 150,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          }
        })
        setQrCodeDataURL(qrDataURL)
      } catch (error) {
        console.error('Error generating QR code:', error)
      }
    },
    onError: (error: any) => {
      console.error('Error generating device registration key:', error)
      notify.error("Couldn't generate the registration key", 'Try again.')
    }
  })

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      notify.success('API key copied')
    }).catch(() => {
      notify.error("Couldn't copy", 'Select the key and copy it by hand.')
    })
  }

  const downloadQR = () => {
    if (!qrCodeDataURL || !deviceApiKey) return

    const link = document.createElement('a')
    link.download = `mgate-device-registration-qr.png`
    link.href = qrCodeDataURL
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          Add Device
        </DialogTitle>
        <DialogDescription>
          Register a new Android device as an SMS gateway.
        </DialogDescription>
      </DialogHeader>

      {isGeneratingKey ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-600">Generating device registration key...</p>
        </div>
      ) : deviceApiKey && qrCodeDataURL ? (
        <div className="space-y-6">
          {/* QR Code Section */}
          <div className="flex flex-col items-center">
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <img
                src={qrCodeDataURL}
                alt="Device Registration QR Code"
                className="w-36 h-36"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 flex items-center gap-2"
              onClick={downloadQR}
            >
              <Download className="h-4 w-4" />
              Download QR Code
            </Button>
          </div>

          {/* API Key Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">
              Device Registration API Key:
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-gray-50 rounded border text-sm font-mono break-all">
                {fullApiKey}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(fullApiKey)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Scan the QR code or copy this key to register your Android device with the redgeGate app
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Next Steps:</h4>
            <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
              <li>Download the redgeGate app on your Android device</li>
              <li>Open the app and tap "Register Device"</li>
              <li>Scan this QR code or paste the API key</li>
              <li>Your device will be connected and ready to send SMS!</li>
            </ol>
          </div>

        </div>
      ) : (
        <div className="text-center py-8">
          <Smartphone className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Add Device</h3>
          <p className="text-gray-600 mb-4">
            Click the button below to generate a unique registration key for your Android device.
          </p>
          <Button onClick={() => generateDeviceKey()}>
            Generate Registration Key
          </Button>
        </div>
      )}
    </>
  )
}

export default function GenerateApiKey() {
  const [open, setOpen] = useState(false)

  const handleDialogChange = (isOpen: boolean) => {
    setOpen(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <Button className="flex items-center gap-2">
          <Smartphone className="h-4 w-4" />
          Add an Android phone
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <SmsDeviceSetupContent onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}
